"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const IMAGE_BASE = "/image/new-version/casting/Sand-Casting/Quality-Assurance";

const GALLERY = [
	{ src: `${IMAGE_BASE}/measuring-machine-1.webp`, altKey: "qualityAssuranceImage1Alt" },
	{ src: `${IMAGE_BASE}/altimeter.webp`, altKey: "qualityAssuranceImage2Alt" },
	{ src: `${IMAGE_BASE}/contour-measuring-instrument-8.jpg`, altKey: "qualityAssuranceImage3Alt" },
	{ src: `${IMAGE_BASE}/cmm.webp`, altKey: "qualityAssuranceImage4Alt" },
] as const;

const FEATURES = [
	{ titleKey: "qualityAssuranceFeature1Title", descKey: "qualityAssuranceFeature1Desc" },
	{ titleKey: "qualityAssuranceFeature2Title", descKey: "qualityAssuranceFeature2Desc" },
	{ titleKey: "qualityAssuranceFeature3Title", descKey: "qualityAssuranceFeature3Desc" },
	{ titleKey: "qualityAssuranceFeature4Title", descKey: "qualityAssuranceFeature4Desc" },
] as const;

const CERTIFICATIONS = [
	{ src: `${IMAGE_BASE}/iso-9001.png`, altKey: "qualityAssuranceCert1Alt" },
	{ src: `${IMAGE_BASE}/iso-13485.png`, altKey: "qualityAssuranceCert2Alt" },
	{ src: `${IMAGE_BASE}/iso-14001.png`, altKey: "qualityAssuranceCert3Alt" },
	{ src: `${IMAGE_BASE}/iatf.png`, altKey: "qualityAssuranceCert4Alt" },
] as const;

export default function QualityAssurance() {
	const t = useTranslations("new-sand-casting");

	return (
		<section className="bg-[#f6ffde] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mx-auto mb-6 max-w-3xl text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("qualityAssuranceTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("qualityAssuranceDesc")}</p>
				</header>

				<div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-12">
					<div>
						<div className="grid grid-cols-2 gap-3 sm:gap-4">
							{GALLERY.map((item) => (
								<div key={item.src} className="relative aspect-[4/2.8] overflow-hidden bg-white">
									<Image
										src={item.src}
										alt={t(item.altKey)}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 50vw, 25vw"
									/>
								</div>
							))}
						</div>

						<div className="mt-6 grid grid-cols-4 gap-3 sm:gap-6">
							{CERTIFICATIONS.map((cert) => (
								<div key={cert.src} className="relative aspect-square">
									<Image
										src={cert.src}
										alt={t(cert.altKey)}
										fill
										className="object-contain"
										sizes="(max-width: 1024px) 25vw, 12vw"
									/>
								</div>
							))}
						</div>
					</div>

					<div>
						<ul className="space-y-5 sm:space-y-6">
							{FEATURES.map((feature) => (
								<li key={feature.titleKey}>
									<h3 className="mb-2 text-lg font-bold text-themePrimary400 sm:mb-3 sm:text-xl">
										{t(feature.titleKey)}
									</h3>
									<p className="text-sm leading-relaxed text-black md:text-base">{t(feature.descKey)}</p>
								</li>
							))}
						</ul>

						<Link
							href="/upload"
							className="mt-6 inline-flex items-center justify-center rounded-md bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#83b751] sm:mt-8 sm:text-base"
						>
							{t("qualityAssuranceCta")}
						</Link>
					</div>
				</div>
			</div>
		</section>
	);
}
